import { tokens } from '@fluentui/react-components';
import { IIndex } from '@syncfusion/ej2-react-grids';
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations';
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import { camelCase } from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReportStatus } from '../../model/reportStatus';
import { customPalette } from '../../theme';
import { getIndexObjectBasedOnCellElement } from '../../utils/TableUtils';

export interface IReportStatusButtonProps {
    disabled?: boolean;
    status: ReportStatus;
    onChange?: (key: ReportStatus) => void;
    isCell?: boolean;
    setSelectedCellIndex?: (element: IIndex | undefined) => void;
    tabIndex?: number;
}

interface IStatusStyleProps {
    $status: ReportStatus;
    disabled?: boolean;
}

const getButtonStyles = (props: IStatusStyleProps) => {
    if (props.disabled) {
        return {
            backgroundColor: '#ffffff',
            border: `1px solid ${tokens.colorNeutralForegroundDisabled}`,
        };
    }

    switch (props.$status) {
        case ReportStatus.InPreparation: {
            return {
                backgroundColor: customPalette.statusLightBlue,
                hoverBackgroundColor: customPalette.statusMidBlue,
                activeBackgroundColor: customPalette.statusDarkBlue,
                border: 'none',
            };
        }
        case ReportStatus.OnHold: {
            return {
                backgroundColor: customPalette.statusLightCyan,
                hoverBackgroundColor: customPalette.statusMidCyan,
                activeBackgroundColor: customPalette.statusDarkCyan,
                border: 'none',
            };
        }
        case ReportStatus.InReview: {
            return {
                backgroundColor: customPalette.statusLightOrange,
                hoverBackgroundColor: customPalette.statusMidOrange,
                activeBackgroundColor: customPalette.statusDarkOrange,
                border: 'none',
            };
        }
        case ReportStatus.Complete: {
            return {
                backgroundColor: customPalette.statusLightGreen,
                hoverBackgroundColor: customPalette.statusMidGreen,
                activeBackgroundColor: customPalette.statusDarkGreen,
                border: 'none',
            };
        }
        default: {
            return {
                backgroundColor: '#ffffff',
                hoverBackgroundColor: customPalette.statusCoolMidGrey,
                activeBackgroundColor: customPalette.statusCoolDarkGrey,
                border: `1px solid ${customPalette.statusCoolLightGrey}`,
            };
        }
    }
};

//had to add this logic in a div because syncfusion has a bug where it clears its styles when status is changing
const StyledStatusDiv = styled.div<{ $status: string; disabled?: boolean }>`
    & button.e-dropdown-btn {
        height: 24px;
        padding: 2px 8px;
        background-color: ${(props) => getButtonStyles(props as IStatusStyleProps).backgroundColor};
        border: ${(props) => getButtonStyles(props as IStatusStyleProps).border};
        border-radius: 18px;
        font-size: 12px;
        color: ${(props) => (props.disabled ? customPalette.grey939393 : '#000000')};
        font-weight: 400;
        @media (forced-colors: active) {
            color: WindowText; // Adjust text color for high contrast
            border: 1px solid WindowText; // Make border visible and adapt to high contrast settings
        }
    }
    & button.e-dropdown-btn:hover {
        background-color: ${(props) => getButtonStyles(props as IStatusStyleProps).hoverBackgroundColor};
    }
    & button.e-dropdown-btn.e-active {
        background-color: ${(props) => getButtonStyles(props as IStatusStyleProps).activeBackgroundColor};
    }
`;

export const ReportStatusButton = (props: IReportStatusButtonProps) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<any>(null);

    const handleCreated = () => {
        if (dropdownRef?.current) {
            const button = dropdownRef.current.element;
            if (button) {
                button.tabIndex = props.tabIndex;
            }
        }
    };

    const handleOnChange = (args: MenuEventArgs) => {
        if (props.onChange) {
            props.onChange(args.item.id as ReportStatus);
        }
    };

    const getIdForDropdownButtonAssociatedWithPopup = (): string | undefined => {
        return document?.activeElement?.closest('div')?.id.replace('-popup', '');
    };

    const getParentCellAsElement = (dropDownId: string): Element | null | undefined => {
        return document?.querySelector('#' + dropDownId)?.closest('.e-rowcell');
    };

    const setFocusOnContainingCell = (dropDownId: string): void => {
        const cell = getParentCellAsElement(dropDownId);
        if (cell) {
            (cell as HTMLElement).click();
        }
    };

    const keepFocusOnParentCell = () => {
        const dropDownId = getIdForDropdownButtonAssociatedWithPopup();

        if (dropDownId) {
            if (props.setSelectedCellIndex) {
                const parentElement = getParentCellAsElement(dropDownId) as HTMLElement;
                props.setSelectedCellIndex(getIndexObjectBasedOnCellElement(parentElement));
            }

            setFocusOnContainingCell(dropDownId);
        }
    };

    const popupBeforeClose = () => {
        if (props.isCell) {
            keepFocusOnParentCell();
        }
    };

    const items = Object.values(ReportStatus).map((x) => ({ text: t(camelCase(x)), id: x }));

    return (
        <StyledStatusDiv $status={props.status} disabled={props.disabled}>
            <DropDownButtonComponent
                created={handleCreated}
                tabIndex={props.tabIndex}
                aria-label={props.status}
                disabled={props.disabled || false}
                items={items}
                select={handleOnChange}
                beforeClose={popupBeforeClose}
                ref={dropdownRef}
            >
                {items.find((x) => x.id === props.status)?.text}
            </DropDownButtonComponent>
        </StyledStatusDiv>
    );
};
